import React, { useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import { grayLight, white } from "globals/design-system/colors";
import { DriverScheduleEvent } from "types";
import { TrashIcon } from "globals/design-system/icons";
import {
  formatDateRangeTitle,
  formatDateTimeToDisplay,
} from "globals/utils/dateFormatters";
import FromDateTimeInput from "./FromDateTimeInput";
import ToDateTimeInput from "./ToDateTimeInput";
import EventTypeSelector from "./EventTypeSelector";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

type DeleteEventDialogProps = {
  open: boolean;
  event: DriverScheduleEvent | null;
  onDeleteEventDialogClose: () => void;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
};

function DeleteEventDialog(props: DeleteEventDialogProps) {
  const { open, event, onDeleteEventDialogClose, startPolling, stopPolling } =
    props;

  // state
  const [confirmDeleteEventDialogOpen, setConfirmDeleteEventDialogOpen] =
    useState(false);

  if (!event) return null;

  // event handlers
  const handleDeleteClick = () => {
    setConfirmDeleteEventDialogOpen(true);
  };

  const handleConfirmDeleteEventDialogClose = () => {
    setConfirmDeleteEventDialogOpen(false);
  };

  // Format dates for display
  const dateRangeTitle = formatDateRangeTitle(
    event.startDateTime,
    event.endDateTime
  );
  const startDateTime = formatDateTimeToDisplay(event.startDateTime);
  const endDateTime = formatDateTimeToDisplay(event.endDateTime);

  return (
    <Dialog
      open={open}
      onClose={onDeleteEventDialogClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 480,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h3" fontWeight={600}>
          {dateRangeTitle}
        </Typography>
        <IconButton onClick={onDeleteEventDialogClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: grayLight }} />

      <DialogContent sx={{ pt: 4 }}>
        {/* Event Type Display */}
        <EventTypeSelector value={event.scheduleType} disabled />

        {/* FROM Date and Time Display */}
        <FromDateTimeInput
          value={startDateTime}
          onDateChange={() => {}}
          onTimeChange={() => {}}
          disabled
        />

        {/* TO Date and Time Display */}
        <ToDateTimeInput
          value={endDateTime}
          onDateChange={() => {}}
          onTimeChange={() => {}}
          disabled
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, justifyContent: "flex-start" }}>
        <Button
          onClick={handleDeleteClick}
          variant="contained"
          color="error"
          sx={{ paddingY: 1, paddingX: 1.5 }}
        >
          <TrashIcon size="small" color={white} />
          <Typography variant="body1" fontWeight={500} ml={0.5} color={white}>
            Delete
          </Typography>
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={confirmDeleteEventDialogOpen}
        onConfirmDeleteEventDialogClose={handleConfirmDeleteEventDialogClose}
        onDeleteEventDialogClose={onDeleteEventDialogClose}
        event={event}
        startPolling={startPolling}
        stopPolling={stopPolling}
      />
    </Dialog>
  );
}

export default DeleteEventDialog;
